import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { KeyRound } from 'lucide-react';
import { Button } from 'components/ui/button';
import { useToast } from 'components/ui/use-toast';
import { cn } from 'lib/utils';
import * as Invite from 'modules/invite';

/** An assessment access code opens its test. */
function usePortalCodeEntry() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [validating, setValidating] = useState(false);

  const submit = async (raw: string): Promise<boolean> => {
    const upper = raw.toUpperCase();
    setValidating(true);
    try {
      const { data } = (await Invite.Api.Validate(upper)) as unknown as {
        data: { testId?: string; id?: string; test?: { id?: string } };
      };
      const testId = data.test?.id || (data.testId ? String(data.testId) : undefined) || data.id;
      if (!testId) throw new Error('no_test');
      navigate(`/portal/tests/take/${testId}?invite=${encodeURIComponent(upper)}`);
    } catch {
      toast.error(t('portal.code_invalid'));
    } finally {
      setValidating(false);
    }
    return false;
  };

  return { submit, busy: validating };
}

interface PortalCodeEntryProps {
  className?: string;
}

/** The bare 44px code field with a key glyph and the lime "Qo'shilish". */
export function PortalCodeEntry({ className }: PortalCodeEntryProps) {
  const { t } = useTranslation();
  const { submit, busy } = usePortalCodeEntry();
  const [code, setCode] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const trimmed = code.trim();

  // The lime button stays live (a disabled one reads washed out and cannot say why); an empty submit returns to the field.
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;
    if (!trimmed) {
      inputRef.current?.focus();
      return;
    }
    if (await submit(trimmed)) setCode('');
  };

  return (
    <form onSubmit={handleSubmit} className={cn('flex w-full gap-2 md:w-auto md:shrink-0', className)}>
      <label className="flex h-11 min-w-0 flex-1 items-center gap-2 rounded-md border border-input bg-card px-3 text-muted-foreground/70 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 md:w-[300px] md:flex-none">
        <KeyRound size={16} className="shrink-0" aria-hidden="true" />
        <input
          ref={inputRef}
          value={code}
          onChange={(event) => setCode(event.target.value)}
          placeholder={t('portal.code_entry_hint')}
          aria-label={t('portal.code_entry_hint')}
          autoComplete="off"
          spellCheck={false}
          className="h-full min-w-0 flex-1 bg-transparent font-mono text-base uppercase text-foreground outline-none focus-visible:!outline-none placeholder:font-sans placeholder:normal-case placeholder:text-muted-foreground md:text-[15px]"
        />
      </label>
      <Button type="submit" size="lg" disabled={busy}>
        {t('portal.join')}
      </Button>
    </form>
  );
}

export default PortalCodeEntry;
